'use client';

import { useEffect, useRef } from 'react';
import type { Chart as ChartType, Plugin } from 'chart.js';
import { barColor, solidColor, TOOLTIP_STYLE } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

type BoxRow = {
  name: string;
  a2: boolean;
  min: number;
  q1: number;
  med: number;
  q3: number;
  max: number;
};

const ESR_YCAP = 0.02;
const esrCols: BoxRow[] = [
  {
    name: 'NAM A1-Standard',
    a2: false,
    min: 1.37632917224964e-7,
    q1: 0.00217843439575981,
    med: 0.00622692678197726,
    q3: 0.0157127291748698,
    max: 1.02680793532591,
  },
  {
    name: 'NAM A2-Full',
    a2: true,
    min: 0.0000190939044841798,
    q1: 0.0011428362922743,
    med: 0.00334233907051384,
    q3: 0.00913467351347208,
    max: 1.00224447250366,
  },
];

const fmtEsr = (v: number) => (v < 0.0001 ? v.toExponential(2) : v.toFixed(4));

export default function A2Esr() {
  const esrRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let chart: ChartType | undefined;

    (async () => {
      const { Chart, registerables } = await import('chart.js');
      if (cancelled) return;
      Chart.register(...registerables);

      if (!esrRef.current) return;

      const esrBoxPlugin: Plugin<'bar'> = {
        id: 'esrBoxOverlay',
        afterDatasetsDraw(c) {
          const ctx = c.ctx;
          const yAxis = c.scales.y;
          const meta = c.getDatasetMeta(0);
          const capW = 6;
          ctx.save();
          esrCols.forEach((d, i) => {
            const bar = meta.data[i];
            if (!bar) return;
            const { x: cx, y: yTop, base: yBottom, width } =
              bar.getProps(['x', 'y', 'base', 'width'], true) as {
                x: number;
                y: number;
                base: number;
                width: number;
              };
            const color = solidColor(d);
            const bw = width;
            const labelX = cx + bw / 2 + 7;
            const yQ1 = yBottom;
            const yQ3 = yTop;
            const yMin = yAxis.getPixelForValue(Math.max(d.min, 0));
            const yMed = yAxis.getPixelForValue(Math.min(d.med, ESR_YCAP));
            const yMax = yAxis.getPixelForValue(Math.min(d.max, ESR_YCAP));
            const overflows = d.max > ESR_YCAP;

            ctx.strokeStyle = color;
            ctx.lineWidth = 1.5;
            ctx.globalAlpha = d.a2 ? 0.35 : 1;
            ctx.beginPath();
            ctx.moveTo(cx, yMin);
            ctx.lineTo(cx, yQ1);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx - capW, yMin);
            ctx.lineTo(cx + capW, yMin);
            ctx.stroke();
            if (overflows) {
              ctx.setLineDash([4, 3]);
            }
            ctx.beginPath();
            ctx.moveTo(cx, yQ3);
            ctx.lineTo(cx, yMax);
            ctx.stroke();
            ctx.setLineDash([]);
            if (!overflows) {
              ctx.beginPath();
              ctx.moveTo(cx - capW, yMax);
              ctx.lineTo(cx + capW, yMax);
              ctx.stroke();
            }
            ctx.globalAlpha = 1;

            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.moveTo(cx - bw / 2, yMed);
            ctx.lineTo(cx + bw / 2, yMed);
            ctx.stroke();

            ctx.fillStyle = d.a2 ? color : 'rgba(160,160,168,0.9)';
            ctx.beginPath();
            ctx.arc(cx, yMed, 3, 0, Math.PI * 2);
            ctx.fill();

            ctx.font = '13px Arial, sans-serif';
            ctx.fillStyle = d.a2 ? color : 'rgba(180,180,180,0.9)';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(fmtEsr(d.med), labelX, yMed);
          });
          ctx.restore();
        },
      };

      chart = new Chart(esrRef.current, {
        type: 'bar',
        data: {
          labels: esrCols.map((d) => d.name),
          datasets: [
            {
              data: esrCols.map((d) => [
                Math.min(d.q1, ESR_YCAP),
                Math.min(d.q3, ESR_YCAP),
              ]),
              backgroundColor: esrCols.map((d) => barColor(d, 0.25)),
              borderColor: esrCols.map((d) => solidColor(d)),
              borderWidth: 1.5,
              borderSkipped: false,
              borderRadius: 3,
              barThickness: 36,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800, easing: 'easeOutQuart' },
          interaction: { mode: 'nearest', axis: 'x', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...TOOLTIP_STYLE,
              callbacks: {
                title: (items) => esrCols[items[0].dataIndex].name,
                label: (ctx) => {
                  const d = esrCols[ctx.dataIndex];
                  return [
                    `Median: ${fmtEsr(d.med)}`,
                    `Q1 – Q3: ${fmtEsr(d.q1)} – ${fmtEsr(d.q3)}`,
                    `Min – Max: ${fmtEsr(d.min)} – ${fmtEsr(d.max)}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.85)',
                autoSkip: false,
              },
            },
            y: {
              min: 0,
              max: ESR_YCAP,
              grid: { color: 'rgba(128,128,128,0.15)' },
              ticks: {
                font: { family: 'Arial', size: 13 },
                color: 'rgba(255,255,255,0.85)',
                callback: (v) => (typeof v === 'number' ? v.toFixed(3) : v),
              },
              title: {
                display: true,
                text: 'ESR',
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.9)',
              },
            },
          },
          layout: { padding: { top: 24, right: 16, left: 0 } },
        },
        plugins: [esrBoxPlugin],
      });
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, []);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="A2 vs A1 Accuracy"
        description="We retrained the entire library of A1 models with A2 and compared the resulting ESR against the original A1 captures (lower is better)."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-6">
        <div className="text-lg font-bold text-white">Retraining ESR Distribution</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">
          Median, Q1/Q3, and min/max whiskers, lower is better
        </div>
        <div className="relative h-[280px] w-full max-[500px]:h-[240px]">
          <canvas ref={esrRef} />
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
